import React, { useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { CardActionArea } from '@mui/material'
import { SxProps, Theme } from '@mui/material/styles'

import DynamicSVGImport from '@/components/ui/icons/DynamicSvgImport'

import colors from '@/assets/scss/_themes-vars.module.scss'

export type DocumentCardProps = {
  /**
   * The extension content of file
   */
  extension: string
  /**
   * Callback fired when image box is clicked
   */
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  /**
   * The primary text content
   */
  primaryText: string
  /**
   * The secondary text content
   */
  secondaryText?: string
  /**
   * The source of file
   */
  src: string
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   * See the [`sx` page](https://mui.com/system/the-sx-prop/) for more details.
   */
  sx?: SxProps<Theme>
}

const textCommonSx: SxProps<Theme> = {
  fontSize: '14px',
  fontWeight: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

export function DocumentCard(props: DocumentCardProps) {
  const { extension, onClick, primaryText, secondaryText, src, sx = [] } = props

  const [blobUrl, setBlobUrl] = useState<string>()

  const isImage = useMemo(() => {
    const imgExts = ['png', 'jpg', 'jpeg']
    return imgExts.includes(extension)
  }, [extension])

  useEffect(() => {
    if (isImage) {
      fetch(src)
        .then((response) => response.blob())
        .then((blob: Blob) => {
          setBlobUrl(URL.createObjectURL(blob))
          // const fileReader = new FileReader()
          // fileReader.onload = () => {
          //   console.log(fileReader.result)
          // }
          // fileReader.readAsDataURL(blob)
        })
        .catch(console.log)
    }
  }, [isImage, src])

  return (
    <Box sx={[{ maxWidth: 280 }, ...(Array.isArray(sx) ? sx : [sx])]}>
      <Card sx={{ width: '100%', backgroundColor: 'transparent', borderRadius: '8px' }} elevation={0}>
        <CardActionArea onClick={onClick}>
          <Box
            component="div"
            sx={[
              {
                width: '100%',
                position: 'relative',
                background: '#FFF',
                borderRadius: '8px',
                cursor: 'pointer',
              },
              isImage &&
                !!blobUrl && {
                  backgroundImage: `url(${blobUrl})`,
                  backgroundSize: 'cover',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                },
            ]}
          >
            <Box
              component="div"
              sx={[
                { pt: '70%', position: 'relative' },
                !isImage && {
                  border: `1px solid ${colors.gray300}`,
                  borderRadius: '8px',
                },
              ]}
            >
              {!isImage && (
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                  <DynamicSVGImport name={extension} width="42px" height="42px" style={{ width: '42px !important', height: '42px !important' }} />
                </Box>
              )}
            </Box>
          </Box>
        </CardActionArea>
      </Card>
      <Stack spacing={0} sx={{ pt: '16px', pb: '8px' }}>
        <Typography gutterBottom variant="h5" component="div" sx={{ ...textCommonSx, color: colors.gray900 }}>
          {primaryText}
        </Typography>
        {secondaryText && (
          <Typography variant="body2" sx={{ ...textCommonSx, color: colors.gray500 }}>
            {secondaryText}
          </Typography>
        )}
      </Stack>
    </Box>
  )
}

export default DocumentCard
